import { useEffect, useState, useRef, type FormEvent, type ChangeEvent } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../store/auth.js";
import { api } from "../../utils/api.js";
import type { ApplicationField } from "@/shared/job-schema.js";
import { isClosedCandidateStage } from "@/shared/candidate-stages.js";

interface JobApplyData {
  slug: string;
  title: string;
  team: string;
  location: string;
  requireUsWorkAuth: boolean;
  applicationFields: ApplicationField[];
}

interface ResumeParseResult {
  name: string | null;
  email: string | null;
  phone: string | null;
  linkedinUrl: string | null;
  yearsExperience: number | null;
  lastTitle: string | null;
}

const RESUME_ACCEPT = ".pdf,application/pdf";

const INPUT_CLASS =
  "mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500";

function DynamicField({ field }: { field: ApplicationField }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700">
        {field.label}
        {field.required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {field.type === "textarea" ? (
        <textarea
          name={field.name}
          required={field.required}
          placeholder={field.placeholder}
          rows={4}
          className={INPUT_CLASS}
        />
      ) : field.type === "select" ? (
        <select name={field.name} required={field.required} className={INPUT_CLASS}>
          <option value="">Select...</option>
          {field.options?.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      ) : field.type === "checkbox" ? (
        <div className="mt-1">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              name={field.name}
              className="rounded border-gray-300 text-primary-700 focus:ring-primary-500"
            />
            {field.placeholder ?? field.label}
          </label>
        </div>
      ) : (
        <input
          type={field.type}
          name={field.name}
          required={field.required}
          placeholder={field.placeholder}
          className={INPUT_CLASS}
        />
      )}
    </div>
  );
}

export function ApplyForm() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  const [job, setJob] = useState<JobApplyData | null>(null);
  const [jobLoading, setJobLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [parseHint, setParseHint] = useState<string | null>(null);
  const [hasActiveApp, setHasActiveApp] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState(user?.email ?? "");
  const [phone, setPhone] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [availableStartDate, setAvailableStartDate] = useState("");
  const [whyGoodFit, setWhyGoodFit] = useState("");
  const [usWorkAuth, setUsWorkAuth] = useState(false);
  const [yearsExperience, setYearsExperience] = useState<number | null>(null);
  const [lastTitle, setLastTitle] = useState<string | null>(null);

  const resumeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!slug) return;
    fetch(`/api/v1/jobs/${slug}`)
      .then((r) => {
        if (!r.ok) throw new Error("Not found");
        return r.json();
      })
      .then((data: JobApplyData) => setJob(data))
      .catch(() => setJob(null))
      .finally(() => setJobLoading(false));

    api
      .get<{ results: { stage: string; jobSlug: string }[] }>("/applications/mine")
      .then((d) => {
        const active = d.results.find((a) => !isClosedCandidateStage(a.stage));
        if (active) setHasActiveApp(true);
      })
      .catch(() => {});
  }, [slug]);

  const handleResumeChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setParsing(true);
    setParseHint(null);
    setYearsExperience(null);
    setLastTitle(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await fetch("/api/v1/applications/parse-resume", {
        method: "POST",
        body: fd,
      });
      if (!resp.ok) {
        setParseHint("Could not parse resume — please fill in the fields below manually.");
        return;
      }
      const result = (await resp.json()) as ResumeParseResult;
      let filled = 0;
      if (result.name) {
        setName((prev) => prev || result.name!);
        filled++;
      }
      if (result.email) {
        setEmail((prev) => (prev === user?.email ? result.email! : prev));
        filled++;
      }
      if (result.phone) {
        setPhone((prev) => prev || result.phone!);
        filled++;
      }
      if (result.linkedinUrl) {
        setLinkedinUrl((prev) => prev || result.linkedinUrl!);
        filled++;
      }
      setYearsExperience(result.yearsExperience ?? null);
      setLastTitle(result.lastTitle ?? null);
      if (filled === 0) {
        setParseHint(
          "We couldn\u2019t extract your details automatically — please fill in the fields below.",
        );
      }
    } catch {
      setParseHint("Could not parse resume — please fill in the fields below manually.");
    } finally {
      setParsing(false);
    }
  };

  if (jobLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-gray-400">Loading...</div>
    );
  }

  if (!job) {
    return (
      <div className="py-12 text-center">
        <h1 className="text-2xl font-bold text-gray-900">Position not found</h1>
        <p className="mt-2 text-gray-500">This role may have been closed or removed.</p>
      </div>
    );
  }

  if (hasActiveApp) {
    return (
      <div className="py-12 text-center">
        <h1 className="text-xl font-semibold text-gray-900">
          You already have an active application
        </h1>
        <p className="mt-3 text-sm text-gray-500">
          Please wait for a decision on your current application before applying to another
          position.
        </p>
        <Link
          to={`/jobs/${slug}`}
          className="mt-4 inline-block text-sm text-primary-700 hover:underline"
        >
          &larr; Back to job details
        </Link>
      </div>
    );
  }

  const requireWorkAuth = job?.requireUsWorkAuth !== false;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (requireWorkAuth && !usWorkAuth) {
      setError(
        "You must confirm your work authorization and that you do not require visa sponsorship.",
      );
      return;
    }
    setSubmitting(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    formData.set("jobSlug", job.slug);
    formData.set("email", email);
    formData.set("name", name);
    formData.set("phone", phone);
    formData.set("linkedinUrl", linkedinUrl);
    formData.set("availableStartDate", availableStartDate);
    formData.set("whyGoodFit", whyGoodFit);
    formData.set("usWorkAuth", usWorkAuth ? "true" : "false");
    if (yearsExperience != null) formData.set("yearsExperience", String(yearsExperience));
    if (lastTitle) formData.set("lastTitle", lastTitle);

    if (resumeRef.current?.files?.[0]) {
      formData.set("resume", resumeRef.current.files[0]);
    }

    try {
      const resp = await fetch("/api/v1/applications", {
        method: "POST",
        body: formData,
      });
      if (!resp.ok) {
        const data = (await resp.json()) as { error?: string };
        throw new Error(data.error ?? "Submission failed");
      }
      navigate(`/jobs/${slug}/confirmation`, { state: { contactEmail: email } });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl px-4 py-8">
      <Link to={`/jobs/${slug}`} className="text-sm text-primary-700 hover:underline">
        &larr; Back to job details
      </Link>

      <div className="mt-6">
        <h1 className="text-2xl font-bold text-gray-900">Apply: {job.title}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {job.team} &middot; {job.location}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mt-8 space-y-6">
        {requireWorkAuth && (
          <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
            <svg
              className="mt-0.5 h-5 w-5 shrink-0 text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-sm text-blue-800">
              This is a <strong>US-based remote position</strong>. Applicants must be authorized to
              work in the United States without requiring employer visa sponsorship.
            </p>
          </div>
        )}

        <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
          <svg
            className="mt-0.5 h-5 w-5 shrink-0 text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="text-sm text-gray-600">
            Our hiring process may include a <strong>take-home assignment</strong>. You are welcome
            to use AI tools when completing it.
          </p>
        </div>

        {/* Resume Upload - first so auto-fill populates fields below */}
        <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-6">
          <h2 className="text-lg font-medium text-gray-900">Resume</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Upload resume <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-500 mb-1">PDF format only</p>
            <input
              type="file"
              name="resume"
              ref={resumeRef}
              required
              accept={RESUME_ACCEPT}
              onChange={handleResumeChange}
              className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:rounded-md file:border-0 file:bg-primary-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-primary-700 hover:file:bg-primary-100"
            />
            {parsing && (
              <p className="mt-1 text-xs text-primary-700 animate-pulse">
                Parsing resume to auto-fill fields...
              </p>
            )}
            {parseHint && !parsing && <p className="mt-1 text-xs text-amber-600">{parseHint}</p>}
          </div>
        </div>

        {/* Standard fields */}
        <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-6">
          <h2 className="text-lg font-medium text-gray-900">Your Information</h2>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Contact Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className={INPUT_CLASS}
            />
            {user?.email && email !== user.email && (
              <p className="mt-1 text-xs text-gray-400">Signed in as {user.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Phone <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              required
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 (555) 123-4567"
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              LinkedIn URL <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              required
              value={linkedinUrl}
              onChange={(e) => setLinkedinUrl(e.target.value)}
              placeholder="https://linkedin.com/in/your-profile"
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Available Start Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              required
              value={availableStartDate}
              onChange={(e) => setAvailableStartDate(e.target.value)}
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Why are you a good fit for this position? <span className="text-red-500">*</span>
            </label>
            <textarea
              required
              rows={5}
              value={whyGoodFit}
              onChange={(e) => setWhyGoodFit(e.target.value)}
              placeholder="Tell us why you're the right person for this role..."
              className={INPUT_CLASS}
            />
          </div>

          {requireWorkAuth && (
            <div>
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={usWorkAuth}
                  onChange={(e) => setUsWorkAuth(e.target.checked)}
                  className="mt-0.5 rounded border-gray-300 text-primary-700 focus:ring-primary-500"
                />
                <span>
                  I confirm that I am currently authorized to work in the United States and do not
                  now, nor will I in the future, require employment visa sponsorship{" "}
                  <span className="text-red-500">*</span>
                </span>
              </label>
            </div>
          )}
        </div>

        {/* Job-specific dynamic fields */}
        {job.applicationFields.length > 0 && (
          <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="text-lg font-medium text-gray-900">Application Questions</h2>
            {job.applicationFields.map((field) => (
              <DynamicField key={field.name} field={field} />
            ))}
          </div>
        )}

        {error && (
          <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-lg bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-[var(--action-primary-hover)] disabled:opacity-50"
        >
          {submitting ? "Submitting..." : "Submit Application"}
        </button>
      </form>
    </div>
  );
}
